/**
 * Tablas del módulo de Redes Sociales sin scroll horizontal: estilos compactos (encabezado y
 * celdas más chicos que studio.th/td, solo en este módulo) y un hook que mide el ancho del
 * contenedor y deja las columnas que caben según su prioridad. Las ocultas se avisan y siguen
 * en el CSV y en la vista de tarjetas.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ink, studio } from '../admin/studioUi';

export const rt = {
    table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' },
    th: {
        ...studio.th,
        padding: '7px 8px',
        fontSize: '0.62rem',
        letterSpacing: '0.04em',
        whiteSpace: 'normal',
        lineHeight: 1.2,
        verticalAlign: 'bottom',
    },
    td: { ...studio.td, padding: '8px', fontSize: '0.84rem' },
    num: { ...studio.td, padding: '8px', fontSize: '0.84rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
};

/**
 * Reparte el ancho: entran primero las columnas sin prioridad y luego por prioridad, mientras
 * la suma de sus `min` quepa. Con ancho 0 (aún sin medir) entran todas.
 * @returns {[Array, Array]} visibles y ocultas, en el orden original
 */
export function fitColumns(columns, width, reserve = 0) {
    if (!width) return [columns, []];
    const ranked = columns
        .map((col, index) => ({ col, index, priority: col.priority ?? -1 }))
        .sort((a, b) => a.priority - b.priority || a.index - b.index);
    let used = reserve;
    const keep = new Set();
    ranked.forEach(({ col, index }) => {
        const min = col.min || 80;
        if (col.priority == null || used + min <= width) {
            keep.add(index);
            used += min;
        }
    });
    return [columns.filter((_, i) => keep.has(i)), columns.filter((_, i) => !keep.has(i))];
}

/**
 * Columnas que caben en el contenedor. Cada columna declara `min` (ancho mínimo estimado en px)
 * y `priority` (menor = más importante; sin prioridad = siempre visible). Se respeta el orden
 * original de las columnas.
 * @returns {[function, Array, Array]} ref (callback) del contenedor, columnas visibles, ocultas
 */
export function useFitColumns(columns, { reserve = 0 } = {}) {
    /* Ref de callback: la tabla puede montarse después (al terminar de cargar o al salir de la vista móvil). */
    const [node, ref] = useState(null);
    const [width, setWidth] = useState(0);
    useEffect(() => {
        if (!node) return undefined;
        const measure = () => setWidth(node.clientWidth);
        measure();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [node]);
    const [visible, hidden] = useMemo(() => fitColumns(columns, width, reserve), [columns, width, reserve]);
    return [ref, visible, hidden];
}

/** Aviso de columnas ocultas por espacio. */
export function HiddenColumnsNote({ hidden }) {
    if (!hidden.length) return null;
    return (
        <p style={{ margin: '8px 0 0', fontSize: '0.74rem', color: ink.faint }}>
            Por espacio no se muestran: {hidden.map(c => c.label).join(', ')}. Amplía la ventana o exporta el CSV para verlas.
        </p>
    );
}
